import React, { useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import LeftSidebar from './LeftSidebar';
import TopAppBar from './TopAppBar';
import Footer from './Footer';
import SkeletonLoader from '../common/SkeletonLoader';
import { useAuth } from '../../hooks/useAuth';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import { TimeoutWarningModal } from '../dialogs/TimeoutWarningModal';
import { cn } from '../../utils/cn';

const AuthenticatedAppLayout: React.FC = () => {
  const { isAuthenticated, isLoading, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [isSidebarPinned, setIsSidebarPinned] = useState(true);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const location = useLocation();

  const isSessionRoute = location.pathname.includes('/session');

  // Trigger warning at 29 minutes of inactivity (1740000ms)
  useSessionTimeout(() => {
    setShowTimeoutWarning(true);
  }, 29 * 60 * 1000);

  const handleKeepAlive = () => {
    setShowTimeoutWarning(false);
    // Dispatch window event to reset inactivity timer hook
    window.dispatchEvent(new Event('pointerdown'));
  };

  const handleForceLogout = async () => {
    setShowTimeoutWarning(false);
    await logout();
  };

  if (isLoading) {
    return <SkeletonLoader type="page" />;
  }

  if (!isAuthenticated && import.meta.env.VITE_MOCK_AUTH !== 'true') {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex h-screen bg-surface">
      <TimeoutWarningModal
        isOpen={showTimeoutWarning}
        onKeepAlive={handleKeepAlive}
        onTimeout={handleForceLogout}
      />
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-20 md:hidden animate-fade-in"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar - hidden on mobile, fixed on desktop */}
      <LeftSidebar 
        isOpen={isSidebarOpen} 
        onClose={() => setIsSidebarOpen(false)} 
        isPinned={isSidebarPinned}
        onPinToggle={() => setIsSidebarPinned(!isSidebarPinned)}
        isHovered={isSidebarHovered}
        onHoverChange={setIsSidebarHovered}
        isSessionView={isSessionRoute}
      />

      {/* Invisible Hover Detector for Left Sidebar */}
      {isSessionRoute && !isSidebarPinned && (
        <div 
          className="fixed left-0 top-0 bottom-0 w-3 z-40"
          onMouseEnter={() => setIsSidebarHovered(true)}
        />
      )}

      {/* Main Content Area */}
      <div 
        className={cn(
          "flex-1 flex flex-col min-w-0 transition-all duration-300",
          isSidebarPinned ? "md:ml-64" : "ml-0"
        )}
      >
        {/* Mobile Header */}
        <TopAppBar onMenuClick={() => setIsSidebarOpen(true)} />

        {/* Content Canvas */}
        <main 
          className={cn(
            "flex-1 flex flex-col relative min-h-0",
            isSessionRoute ? "overflow-hidden h-full" : "overflow-y-auto overflow-x-hidden"
          )}
        >
          {isSessionRoute ? (
            <Outlet />
          ) : (
            <div className="flex-1 w-full max-w-7xl mx-auto px-6 py-8 md:px-10 md:py-12">
              <Outlet />
            </div>
          )}
          
          {!isSessionRoute && <Footer />}
        </main>
      </div>
    </div>
  );
};

export default AuthenticatedAppLayout;
